/**
 * Settings → Attributes → one attribute → "AI for this value" (migration 920).
 *
 * Off, AI recommends, or AI provides — and the agent that does it. "Provides"
 * is a standing acceptance rule in the admin's own name: the agent's value is
 * accepted when the agent has been right at least the floor's share of the time
 * on this value, as judged by people over at least 30 reviews. Until then it
 * only recommends. Admins change it; everyone else sees what it is.
 */

import { useEffect, useState } from 'react';
import { Bot } from 'lucide-react';
import { Field, Select, TextInput } from '@/components/ds';
import { btnPrimary } from '@/components/mono/primitives';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useValueAiSettingsStore } from '@/stores/prioritization/valueAiSettingsStore';
import { setValueAiSetting } from '@/lib/valueAiService';
import { VALUE_AI_MODE_LABEL, type ValueAiMode } from '@/lib/valueStatus';
import { loadAgentCalibration, MIN_REVIEWS_FOR_CALIBRATION, type CalibrationRow } from '@/lib/agentCalibrationService';

const MODE_HINT: Record<ValueAiMode, string> = {
  off: 'No agent works on this value. People set it.',
  recommend: 'The agent proposes a value; a person accepts, changes or rejects it.',
  provide:
    'The agent’s value is accepted in your name when it has proven reliable here. Every value set this way is marked, and anyone can change it.',
};

export function ValueAiSettingCard({ attributeKey, attributeLabel }: { attributeKey: string; attributeLabel: string }) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const can = useUserStore((s) => s.can);
  const isAdmin = can('admin.settings');
  const load = useValueAiSettingsStore((s) => s.load);
  const setting = useValueAiSettingsStore((s) => s.settingFor(attributeKey));
  useValueAiSettingsStore((s) => s.settings);
  const agents = useValueAiSettingsStore((s) => s.agents);
  const agentName = useValueAiSettingsStore((s) => s.agentName);
  const put = useValueAiSettingsStore((s) => s.put);

  const [mode, setMode] = useState<ValueAiMode>('off');
  const [agent, setAgent] = useState<string>('value_estimator');
  const [floor, setFloor] = useState('80');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [calibration, setCalibration] = useState<CalibrationRow[]>([]);

  useEffect(() => {
    if (workspaceId) void load(workspaceId);
  }, [workspaceId, load]);

  useEffect(() => {
    setMode(setting?.mode ?? 'off');
    setAgent(setting?.agentType ?? 'value_estimator');
    setFloor(String(Math.round((setting?.acceptFloor ?? 0.8) * 100)));
  }, [setting]);

  useEffect(() => {
    if (!workspaceId || mode === 'off') return;
    loadAgentCalibration(workspaceId, agent).then(setCalibration).catch(() => setCalibration([]));
  }, [workspaceId, agent, mode]);

  const rows = calibration.filter((r) => r.field === attributeKey);
  const reliability = rows.length
    ? rows
        .map((r) => {
          const effective = r.reviewedCount - r.expiredCount;
          return effective >= MIN_REVIEWS_FOR_CALIBRATION && r.acceptedRate != null
            ? `${r.bucket} confidence: right ${Math.round(r.acceptedRate * 100)}% of the time over ${effective} reviews`
            : `${r.bucket} confidence: ${effective} of ${MIN_REVIEWS_FOR_CALIBRATION} reviews needed`;
        })
        .join(' · ')
    : `No reviews of ${agentName(agent)} on ${attributeLabel} yet — it needs ${MIN_REVIEWS_FOR_CALIBRATION} before it can provide.`;

  async function save() {
    if (!workspaceId) return;
    setSaving(true);
    setMessage(null);
    const f = Number(floor);
    const { setting: saved, error } = await setValueAiSetting({
      workspaceId,
      attributeKey,
      mode,
      agentType: mode === 'off' ? null : agent,
      acceptFloor: Number.isFinite(f) && f > 0 ? Math.min(100, f) / 100 : 0.8,
      reason: reason.trim() || null,
    });
    setSaving(false);
    if (error || !saved) return setMessage({ tone: 'error', text: error ?? 'Nothing was saved.' });
    put(saved);
    setReason('');
    setMessage({ tone: 'ok', text: `Saved: ${VALUE_AI_MODE_LABEL[saved.mode]}${saved.agentType ? ` — ${agentName(saved.agentType)}` : ''}.` });
  }

  const current = setting
    ? `${VALUE_AI_MODE_LABEL[setting.mode]}${setting.agentType ? ` — ${agentName(setting.agentType)}` : ''}${
        setting.mode === 'provide' ? ` when right at least ${Math.round(setting.acceptFloor * 100)}% of the time` : ''
      }`
    : VALUE_AI_MODE_LABEL.off;

  return (
    <div className="card" data-testid="value-ai-setting">
      <div className="mb-2 flex items-center gap-2">
        <Bot className="h-5 w-5 text-[var(--text-ter)]" />
        <h2 className="text-lg font-semibold text-[var(--text-pri)]">AI for this value</h2>
      </div>
      <p className="mb-4 text-sm text-[var(--text-ter)]">
        Now: <strong className="text-[var(--text-sec)]">{current}</strong>
        {setting?.setAt && setting.setBy ? ` · set ${new Date(setting.setAt).toLocaleDateString()}` : ''}
        {setting?.reason ? ` · “${setting.reason}”` : ''}
      </p>

      {isAdmin ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="AI for this value">
            {(['off', 'recommend', 'provide'] as ValueAiMode[]).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                onClick={() => setMode(m)}
                className={`border px-3 py-1.5 text-sm ${
                  mode === m
                    ? 'border-[var(--slate)] bg-[var(--slate-dim)] font-medium text-[var(--text-pri)]'
                    : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                }`}
              >
                {VALUE_AI_MODE_LABEL[m]}
              </button>
            ))}
          </div>
          <p className="text-sm text-[var(--text-sec)]">{MODE_HINT[mode]}</p>

          {mode !== 'off' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Agent">
                <Select size="sm" value={agent} onChange={(e) => setAgent(e.target.value)}
                  options={agents.map((a) => ({ value: a.agentType, label: a.name }))} />
              </Field>
              {mode === 'provide' && (
                <Field label="Accept when right at least (%)" hint="Judged by people's reviews of this agent on this value.">
                  <TextInput size="sm" inputMode="numeric" value={floor} onChange={(e) => setFloor(e.target.value)} />
                </Field>
              )}
            </div>
          )}
          {mode !== 'off' && <p className="text-xs text-[var(--text-ter)]">Reliability so far: {reliability}</p>}

          <Field label="Why (optional)">
            <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. the estimator has been right on effort for two quarters" />
          </Field>

          {message && (
            <p className={`text-sm ${message.tone === 'error' ? 'text-[var(--coral)]' : 'text-[var(--text-sec)]'}`}>{message.text}</p>
          )}
          <div className="flex justify-end">
            <button type="button" disabled={saving} onClick={() => void save()} {...btnPrimary('disabled:opacity-50')}>
              {saving ? 'Saving…' : 'Save AI setting'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[var(--text-ter)]">Changing how AI helps with this value needs an admin.</p>
      )}
    </div>
  );
}
